// IndexedDB Storage Engine for Nexus IPTV
// Handles high-capacity storage (tens of thousands of channels, movies, and series)
// Bypasses browser 5MB localStorage limits smoothly.

const DB_NAME = 'NexusIPTV_DB';
const DB_VERSION = 1;

export interface StorageCatalog {
  userChannels: any[];
  systemChannels: any[];
  movies: any[];
  series: any[];
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error('IndexedDB not supported in this browser'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create object stores if they don't exist
      if (!db.objectStoreNames.contains('app_config')) {
        db.createObjectStore('app_config');
      }
      if (!db.objectStoreNames.contains('catalogs')) {
        db.createObjectStore('catalogs');
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function setIDBItem<T>(storeName: 'app_config' | 'catalogs', key: string, value: T): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const req = store.put(value, key);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    console.warn(`[IndexedDB] setIDBItem error for ${key}:`, e);
  }
}

export async function getIDBItem<T>(storeName: 'app_config' | 'catalogs', key: string): Promise<T | null> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const req = store.get(key);
      req.onsuccess = () => resolve((req.result as T) || null);
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    console.warn(`[IndexedDB] getIDBItem error for ${key}:`, e);
    return null;
  }
}

export async function deleteIDBItem(storeName: 'app_config' | 'catalogs', key: string): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const req = store.delete(key);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    console.warn(`[IndexedDB] deleteIDBItem error for ${key}:`, e);
  }
}

export async function clearIDBStore(storeName: 'app_config' | 'catalogs'): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const req = store.clear();
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    console.warn(`[IndexedDB] clearIDBStore error for ${storeName}:`, e);
  }
}
